/**
 * Google Places via Maps JavaScript API (Places library).
 * Browser-safe alternative to iOS Places API (New) REST.
 */

import type { ItineraryItemType } from '../types/client';
import { isGoogleMapsJsConfigured, loadGoogleMaps } from '../utils/loadGoogleMaps';

export interface PlaceSearchResult {
  id: string;
  name: string;
  address: string;
  types: string[];
  latitude: number;
  longitude: number;
  rating?: number;
}

export interface PlaceDetails {
  id: string;
  name: string;
  address: string;
  phone?: string;
  website?: string;
  types: string[];
  latitude: number;
  longitude: number;
  rating?: number;
}

export class GooglePlacesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GooglePlacesError';
  }
}

const MEAL_TYPES = ['restaurant', 'cafe', 'bar', 'bakery', 'food', 'meal_takeaway', 'meal_delivery'];
const TRANSPORT_TYPES = [
  'transit_station',
  'airport',
  'train_station',
  'bus_station',
  'subway_station',
  'light_rail_station',
];

export function isGooglePlacesConfigured(): boolean {
  return isGoogleMapsJsConfigured();
}

/** Infer meal / transport / activity from Google place types (mirrors iOS). */
export function inferItemType(types: string[]): Exclude<ItineraryItemType, 'accommodation' | 'other' | 'note'> {
  const lower = types.map((t) => t.toLowerCase());
  if (lower.some((t) => MEAL_TYPES.some((m) => t.includes(m)))) return 'meal';
  if (lower.some((t) => TRANSPORT_TYPES.some((m) => t.includes(m)))) return 'transport';
  return 'activity';
}

function readLatLng(location: google.maps.places.Place['location']): { lat: number; lng: number } | null {
  if (!location) return null;
  if (typeof (location as { lat: unknown }).lat === 'function') {
    const loc = location as { lat: () => number; lng: () => number };
    return { lat: loc.lat(), lng: loc.lng() };
  }
  const lit = location as google.maps.LatLngLiteral;
  if (typeof lit.lat === 'number' && typeof lit.lng === 'number') {
    return { lat: lit.lat, lng: lit.lng };
  }
  return null;
}

function placeIdFromResource(id: string | null | undefined): string {
  if (!id) return '';
  // Places (New) sometimes returns "places/ChIJ..."
  return id.startsWith('places/') ? id.slice('places/'.length) : id;
}

async function getPlacesLibrary(): Promise<typeof google.maps.places> {
  if (!isGooglePlacesConfigured()) {
    throw new GooglePlacesError('noAPIKey');
  }
  await loadGoogleMaps();
  const lib = (await google.maps.importLibrary('places')) as typeof google.maps.places;
  return lib;
}

export interface SearchPlacesParams {
  textQuery: string;
  cityName: string;
  latitude?: number;
  longitude?: number;
  maxResults?: number;
}

export async function searchPlaces(params: SearchPlacesParams): Promise<PlaceSearchResult[]> {
  const query = params.textQuery.trim();
  if (!query) return [];

  const { Place } = await getPlacesLibrary();
  const cityName = params.cityName.trim() || 'nearby';
  const textQuery = `${query} in ${cityName}`;
  const maxResultCount = Math.min(Math.max(params.maxResults ?? 15, 1), 20);

  const hasBias =
    typeof params.latitude === 'number' &&
    typeof params.longitude === 'number' &&
    Number.isFinite(params.latitude) &&
    Number.isFinite(params.longitude) &&
    !(params.latitude === 0 && params.longitude === 0);

  try {
    const request: google.maps.places.SearchByTextRequest = {
      textQuery,
      fields: ['id', 'displayName', 'formattedAddress', 'types', 'location', 'rating'],
      maxResultCount,
      language: 'en',
    };
    if (hasBias) {
      request.locationBias = {
        center: { lat: params.latitude as number, lng: params.longitude as number },
        radius: 20000,
      };
    }

    const { places } = await Place.searchByText(request);
    if (!places?.length) return [];

    return places
      .map((place): PlaceSearchResult | null => {
        const coords = readLatLng(place.location);
        const id = placeIdFromResource(place.id);
        if (!id) return null;
        return {
          id,
          name: place.displayName?.trim() || 'Unknown',
          address: place.formattedAddress?.trim() || '',
          types: place.types ?? [],
          latitude: coords?.lat ?? 0,
          longitude: coords?.lng ?? 0,
          rating: typeof place.rating === 'number' ? place.rating : undefined,
        };
      })
      .filter((p): p is PlaceSearchResult => p != null);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new GooglePlacesError(message || 'Search failed');
  }
}

export async function getPlaceDetails(placeId: string): Promise<PlaceDetails> {
  const id = placeIdFromResource(placeId);
  if (!id) throw new GooglePlacesError('Invalid place id');

  const { Place } = await getPlacesLibrary();

  try {
    const place = new Place({ id });
    await place.fetchFields({
      fields: [
        'id',
        'displayName',
        'formattedAddress',
        'nationalPhoneNumber',
        'internationalPhoneNumber',
        'websiteURI',
        'types',
        'location',
        'rating',
      ],
    });

    const coords = readLatLng(place.location);
    const phone =
      place.nationalPhoneNumber?.trim() ||
      place.internationalPhoneNumber?.trim() ||
      undefined;

    return {
      id: placeIdFromResource(place.id) || id,
      name: place.displayName?.trim() || 'Unknown',
      address: place.formattedAddress?.trim() || '',
      phone,
      website: place.websiteURI?.trim() || undefined,
      types: place.types ?? [],
      latitude: coords?.lat ?? 0,
      longitude: coords?.lng ?? 0,
      rating: typeof place.rating === 'number' ? place.rating : undefined,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new GooglePlacesError(message || 'Failed to load place details');
  }
}
